/**
 * @module GameObjectTools
 * 
 * Run `setCommonFeatures()` to get the full set of features
 * onto a game object.
 */

import ComponentManager from "./component-manager.js";
import PropertySwitchboard from "./property-switchboard.js";
import TagManager from "./tag-manager.js";

const GameObjectTools = {


    /**
     * Map of events that may be dispatched during the object's life.
     * 
     * `COLLIDE` will be invoked with another object as a parameter.
     * 
     * The `UPDATE`, `PRE_UPDATE`, and `POST_UPDATE` events will be
     * invoked with delta and time parameters.
     * 
     * @property {object} EVENTS
     * @static
     */
    _EVENTS: {
        ANIMATION_STOP: "animationstop",
        COLLIDE: "collide",
        CREATE: "create",
        LEVEL_START: "levelStart",
        LEVEL_SWITCH: "levelSwitch",
        PRE_UPDATE: "preUpdate",
        UPDATE: "update",
        POST_UPDATE: "postUpdate",
    },

    /**
     * Destroy game object in a safe fashion.
     */
    _fDestroySafe: function () {
        if ( this.exists ) {
            this.exists = false;
            if ( this.setPhysics ) {
                this.setPhysics( false );
            }
            if ( this.physicsLayer ) {
                this.physicsLayer.remove( this );
            }
            if ( this.properties instanceof PropertySwitchboard ) {
                this.properties.clear();
            }
        }
    },

    /**
     * Return bounds based on the object's hitbox.
     * @returns {Phaser.Geom.Rectangle}
     */
    _fGetHitboxFromBody: function () {
        // This calculation is derived from
        // `Phaser.Arcade.Body#preUpdate`/`Phaser.Arcade.Body#updateBounds`.
        // We don't use those functions themselves,
        // because they would mutate the actual hitbox mid-frame
        // and cause all sorts of messes.
        const body = this.body;
        const m = this.getWorldTransformMatrix( this._hitboxMatrix );
        const msx = m.scaleX;
        const msy = m.scaleY;
        const offset = body.offset;
        this._hitbox.setTo(
            m.tx + msx * ( offset.x - this.displayOriginX ),
            m.ty + msy * ( offset.y - this.displayOriginY ),
            body.sourceWidth * msx,
            body.sourceHeight * msy
        );
        return this._hitbox;
    },

    /**
     * Returns bounds based on the object's inherent properties.
     * @returns {Phaser.Geom.Rectangle}
     */
    _fGetHitboxFromMyself: function () {
        this._hitbox.setTo(
            this.posX,
            this.posY,
            this.width,
            this.height );
        return this._hitbox;
    },

    /**
     * Perform level-start operations. The level-start occurs
     * after all game objects have been created and initialized.
     * It is the event that signals it is safe for scripts to look for
     * other game objects.
     */
    _fLevelStart: function () {
        this.emit( this.EVENTS.LEVEL_START );
    },

    /**
     * Perform level-switch operations.
     * This occurs when the level is switched via the LevelManager.
     * @param {Number} target - index of target level
     */
    _fLevelSwitch: function ( target ) {
        this.emit( this.EVENTS.LEVEL_SWITCH );
    },

    /**
     * Assign components to this object from a `gamefroot-4` Instance
     * resource's `scripts` property.
     * @param {Object} instance - instance description with script dependencies
     */
    _fSetupPluginComponents: function ( instance ) {
        if ( !(
            instance &&
            Array.isArray( instance.dependencies ) &&
            Array.isArray( instance.scripts ) ) )
        {
            return console.error( "Invalid scripts on instance", instance );
        }
        const rs = this.scene.game.resourceStore;
        instance.scripts.forEach( script => {
            const scriptResource = rs.getExternalResource(instance, script.id);
            if ( !(
                scriptResource &&
                Array.isArray( scriptResource.variables ) ) )
            {
                return console.error(
                    "Cannot find script", script, "on instance", instance );
            }
            
            // Compose parameters for instantiation.
            // Prioritise the instance variables over defaults.
            const params = {};
            scriptResource.variables.forEach( variable => {
                params[ variable.name ] = variable.varDefault;
            } );
            script.variables.forEach( variable => {
                params[ variable.name ] = variable.value;
            } );
            
            // Special initialization for certain variable types.
            const children = this.scene.children.getChildren();
            scriptResource.variables.forEach( variable => {
                let resource;
                switch (variable.varType) {
                    case "Sound":
                        // Hook up audio resources.
                        // Convert internal IDs to external, absolute IDs.
                        // We wouldn't do this in the Editor,
                        // because external IDs change,
                        // but here the data is static.
                        resource = rs.getExternalResource(
                            instance, params[ variable.name ] );
                        if (resource) {
                            params[ variable.name ] = resource.id;
                        }
                        break;
                    case "JSON":
                        // Hook up JSON resources.
                        // References are stored as a dependency ID.
                        // Pass a copy of the original JSON data to the script.
                        resource = rs.getExternalResource(
                            instance, params[ variable.name ] );
                        if (resource) {
                            params[ variable.name ] =
                                JSON.parse(JSON.stringify(resource.json));
                        }
                        break;
                    case "Instance":
                        // Hook up instance resources.
                        // Instances are stored as a dependency ID.
                        // We resolve this to a GameObject.
                        // They may not exist on level create,
                        // so we will repeat this step immediately prior
                        // to level start.
                        resource = rs.getExternalResource(
                            instance, params[ variable.name ] );
                        if (resource) {
                            const id = `resource.id`;
                            let instance = children.find(child => {
                                return child.getData("id") == id;
                            });
                            if (instance) {
                                params[ variable.name ] = instance;
                            }
                        }
                        break;
                }
            } );
            
            // Don't rename the params. Blockly-compiled plugins expect
            // the original names and convert them to JavaScript variable names
            // in situ.

            try {
                Phaserfroot.PluginManager.instantiate(
                    scriptResource.id,
                    this,
                    params );
            } catch ( error ) {
                console.error("Syntax error on instance:", instance, "script instance", script, "script", scriptResource, "error", error);
                const name = scriptResource ? scriptResource.name : "untitled";
                this.scene.game.reportError(
                    "Syntax error in script [" + name + "].\n\n" +
                    "You haven't done anything wrong. We must have made a mistake while putting the blocks together. You might be able to fix the problem by removing recently added blocks.\n\n" +
                    "This is a Gamefroot bug - please let us know about it!" );
            }
        } );
    },

    /**
     * Set whether physics is active on this game object.
     * @param {boolean} value - whether physics should be active
     */
    _fSetPhysics: function ( value ) {
        this.body.enable = value;
    },

    /**
     * Update the bounds of a physics body.
     */
    // MODIFIED
    // Hack into `updateBounds` to add functionality.
    // This adds code to the end of the function.
    // If the sprite's scale is negative, adjust the hitbox position,
    // because Phaser can't deal with negative scales.
    _fUpdateBounds: new Function(
        Phaser.Physics.Arcade.Body.prototype.updateBounds
        .toString()
        .replace( /function[\s]*\(\)[\s]*\{/, "" )
        .slice( 0, -1 ) +
        ";if(this.transform.scaleX<0){this.transform.x-=this.width}if(this.transform.scaleY<0){this.transform.y-=this.height}" ),

    /**
     * Return the names that Blockly will have asigned to variables
     * during compilation in a Script resource.
     * @param {Script} plugin - gf4 format script definition
     * @returns {Map} Map from literal key to safe key
     */
    getSafePluginVariableNames: function (plugin) {
        const renamed = new Map();
        plugin.variables.forEach(variable => {
            renamed.set(variable.name, variable.name);
        });
        renamed.forEach((value, key) => {
            // Eliminate invalid characters.
            value = value.replace(/[^_A-Za-z0-9]/gi, "_");
            // Compensate for invalid starts by prefixing "my_".
            if (value.match(/^[^_A-Za-z]/)) {
                value = "my_" + value;
            }
            renamed.set(key, value);
        });
        // Compensate for duplicates by suffixing a number.
        const extant = new Set();
        renamed.forEach((value, key) => {
            let suffix = 1;
            while (extant.has(value)) {
                value = value + suffix++;
            }
            renamed.set(key, value);
        });
        return renamed;
    },

    /**
     * Add anchor systems to game object.
     * @param {Object} gameObject - entity to which to add properties
     */
    setAnchor: function ( gameObject ) {

        /**
         * Internal value, used to generate `posX` values
         * that account for non-zero `originX`.
         * @type {Number}
         * @private
         */
        gameObject._anchorX = 0;

        /**
         * Internal value, used to generate `posY` values
         * that account for non-zero `originY`.
         * @type {Number}
         * @private
         */
        gameObject._anchorY = 0;

        /**
         * Internal value, used to track cumulative rotation.
         * @type {Number}
         * @private
         */
        gameObject._bearing = gameObject.angle;

        Object.defineProperties( gameObject, {
            /**
             * Horizontal anchor point, in pixels.
             * @type {Number}
             */
            anchorX: {
                enumerable: true,
                get: function() {
                    return this._anchorX;
                },
                set: function( value ) {
                    if ( this.width === 0 ) {
                        this.originX = 0;
                    } else {
                        this.originX = value / this.width;
                    }
                    this.x += value - this._anchorX;
                    this._anchorX = value;
                    this.updateDisplayOrigin();
                },
            },
            /**
             * Vertical anchor point, in pixels.
             * @type {Number}
             */
            anchorY: {
                enumerable: true,
                get: function() {
                    return this._anchorY;
                },
                set: function( value ) {
                    if ( this.height === 0 ) {
                        this.originY = 0;
                    } else {
                        this.originY = value / this.height;
                    }
                    this.y += value - this._anchorY;
                    this._anchorY = value;
                    this.updateDisplayOrigin();
                },
            },
            /**
             * Angle of the object, in degrees, without wrapping to a
             * normalized range. Access this for rotation by preference.
             * @type {Number}
             */
            bearing: {
                // TODO: Automatic functions like physics which affect rotation
                // will not affect this. Find a solution.
                enumerable: true,
                get: function () {
                    return this._bearing;
                },
                set: function ( value ) {
                    this._bearing = value;
                    this.angle = value;
                },
            },
            /**
             * Effective horizontal position, accounting for modified origin.
             * @type {Number}
             */
            posX: {
                enumerable: true,
                get: function() {
                    return this.x - this._anchorX;
                },
                set: function( value ) {
                    this.x = value + this._anchorX;
                },
            },
            /**
             * Effective vertical position, accounting for modified origin.
             * @type {Number}
             */
            posY: {
                enumerable: true,
                get: function() {
                    return this.y - this._anchorY;
                },
                set: function( value ) {
                    this.y = value + this._anchorY;
                },
            },
        } );
    },

    /**
     * Add common features used in Phaserfroot, including:
     * - Anchor systems
     * - `exists` flag
     * @param {Object} gameObject - entity to which to add common features
     */
    setCommonFeatures: function ( gameObject ) {

        /**
         * Whether this object exists in the game level;
         * that is, whether it has not been destroyed yet.
         * @type boolean
         * @readonly
         */
        gameObject.exists = true;
        gameObject.EVENTS = this._EVENTS;
        gameObject.colliders = [];

        if ( !gameObject.isTilemap ) {
            // Don't apply physics to tilemaps. They work differently.
            this.setPhysics( gameObject );
        }

        this.setAnchor( gameObject );
        this.setFunctions( gameObject );
        this.setGetHitbox( gameObject );
        this.setManagers( gameObject );
    },

    /**
     * Add common functions to an object, allowing it to react properly
     * to expected Phaserfroot events.
     * @param {Object} gameObject - entity to which to add features
     */
    setFunctions: function ( gameObject ) {
        gameObject.destroySafe = this._fDestroySafe;
        gameObject.onLevelStart = this._fLevelStart;
        gameObject.onLevelSwitch = this._fLevelSwitch;
        if ( !gameObject.isTilemap ) {
            // Don't apply physics to tilemaps. They work differently.
            gameObject.setPhysics = this._fSetPhysics;
        }
        gameObject.setupPluginComponents = this._fSetupPluginComponents;
    },

    /**
     * Add a `getBounds` method to an object.
     * This is an optimised bounds getter.
     * @param {Object} gameObject - entity to which to add features
     */
    setGetHitbox: function ( gameObject ) {
        gameObject._hitbox = new Phaser.Geom.Rectangle();
        gameObject._hitboxMatrix = new Phaser.GameObjects.Components.TransformMatrix();
        if ( gameObject.body ) {
            gameObject.getHitbox = this._fGetHitboxFromBody;
        } else {
            gameObject.getHitbox = this._fGetHitboxFromMyself;
        }
    },

    /**
     * Add common managers to an object, including:
     * - Component manager
     * - Tag manager
     * - Property switchboard
     * @param {Object} gameObject - entity to which to add features
     */
    setManagers: function ( gameObject ) {
        this.setManagerComponents( gameObject );
        this.setManagerProperties( gameObject );
        this.setManagerTags( gameObject );
    },

    /**
     * Add a component manager to the entity.
     * @param {Object} gameObject - entity to which to add features
     */
    setManagerComponents: function ( gameObject ) {
        /**
         * Component manager for automated, modular updates
         * @type {ComponentManager}
         */
        gameObject.components = new ComponentManager( gameObject );
    },

    /**
     * Add a property switchboard to the entity.
     * @param {Object} gameObject - entity to which to add features
     */
    setManagerProperties: function ( gameObject ) {
        /**
         * Property manager for handling keys and messages
         * @type {PropertySwitchboard}
         */
        gameObject.properties = new PropertySwitchboard();
    },

    /**
     * Add a tag manager to the entity.
     * @param {Object} gameObject - entity to which to add features
     */
    setManagerTags: function ( gameObject ) {
        /**
         * Tag manager for tracking special identifiers
         * @type {TagManager}
         */
        gameObject.tags = new TagManager();
    },

    /**
     * Integrate the gameObject with the scene's physics.
     * @param {Object} gameObject - entity to which to add features
     */
    setPhysics: function ( gameObject ) {
        const scene = gameObject.scene;
        scene.physics.world.enableBody(
            gameObject, Phaser.Physics.Arcade.DYNAMIC_BODY );

        // Objects do not start with gravity enabled by default.
        gameObject.body.allowGravity = false;

        // Physics layer to which this object belongs.
        gameObject.physicsLayer = undefined;
        scene.physicsLayersManager.addToLayer( gameObject, 0 );

        gameObject.body.updateBounds = this._fUpdateBounds;
    },
};
export default GameObjectTools;
